import { useState, useMemo } from "react";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { subirDatos, eliminarDato } from "./syncSupabase";

// ════════════════════════════════════════════════════════════
// ÉTICA Y RSE — hub único de evidencia para cualquier esquema de
// auditoría social/ética (Sistema B / B Impact Assessment, SMETA,
// Disney FAMA, Universal, y similares). Ninguno de esos cuestionarios
// oficiales es público ni fijo — cada uno tiene su propio formulario
// propietario y se adapta según tamaño/sector/mercado de la empresa.
// Por eso este módulo NO replica ningún formulario oficial: junta y
// ordena, por dimensión de impacto (Gobernanza / Trabajadores /
// Comunidad / Ambiente / Clientes), la evidencia real de la operación
// — automatizada donde ya existe en otros módulos, manual donde no —
// para que esté lista y mapeable a cualquier esquema puntual que se
// presente.
// ════════════════════════════════════════════════════════════

const Q = {
  azul: "#1C3FAA", azulVivo: "#2A7FFF", azulMedio: "#6B6FD6",
  grisClaro: "#E8E9F6", grisPalido: "#F4F6FB", grisOscuro: "#2B2F36",
  grisPlata: "#8E93A1", blanco: "#FFFFFF",
  verde: "#16a34a", verdeClaro: "#22c55e",
};

const STORAGE_ITEMS = "alqos_eticarse_items";
const STORAGE_DIVULGACION = "alqos_eticarse_divulgacion";

const storage = {
  get: (k, def = []) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(def)); } catch { return def; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { console.error(e); } },
};

const DIMENSIONES = {
  gobernanza:    { label: "Gobernanza",   icono: "⚖️", color: "#6B6FD6" },
  trabajadores:  { label: "Trabajadores", icono: "👥", color: "#2A7FFF" },
  comunidad:     { label: "Comunidad",    icono: "🤝", color: "#f59e0b" },
  ambiente:      { label: "Ambiente",     icono: "🌿", color: "#22c55e" },
  clientes:      { label: "Clientes",     icono: "🛒", color: "#e63946" },
};

const ESTADOS = {
  cumple:     { label: "✅ Cumple",       color: "#22c55e", puntos: 1 },
  parcial:    { label: "🟡 Parcial",      color: "#f59e0b", puntos: 0.5 },
  no_cumple:  { label: "🔴 No cumple",    color: "#e63946", puntos: 0 },
  na:         { label: "➖ N/A",          color: "#808085", puntos: null },
  pendiente:  { label: "⬜ Pendiente",    color: "#8E93A1", puntos: 0 },
};

// Ítems de referencia propios de ALQOS™ — inspirados en los temas que
// comparten los esquemas mencionados arriba, NO son el cuestionario
// oficial de ninguno de ellos. `fuenteSugerida` indica de qué módulo
// se puede traer evidencia automáticamente; "manual" = no hay módulo
// de origen todavía, se carga acá directo.
const ITEMS_SEED = [
  // ── GOBERNANZA ──
  { dimension: "gobernanza", tema: "Misión y compromiso social declarado", peso: 2, fuenteSugerida: "manual" },
  { dimension: "gobernanza", tema: "Código de ética / conducta escrito y comunicado", peso: 2, fuenteSugerida: "manual" },
  { dimension: "gobernanza", tema: "Canal de denuncias (ética, acoso, corrupción)", peso: 3, fuenteSugerida: "manual" },
  { dimension: "gobernanza", tema: "Prácticas anticorrupción documentadas", peso: 2, fuenteSugerida: "manual" },
  { dimension: "gobernanza", tema: "Estructura de decisión y responsables documentados", peso: 1, fuenteSugerida: "manual" },
  { dimension: "gobernanza", tema: "Cumplimiento normativo general (matriz legal)", peso: 2, fuenteSugerida: "cumplimiento" },
  // ── TRABAJADORES ──
  { dimension: "trabajadores", tema: "Libertad de asociación y negociación colectiva", peso: 2, fuenteSugerida: "manual" },
  { dimension: "trabajadores", tema: "No discriminación en contratación / promoción", peso: 2, fuenteSugerida: "manual" },
  { dimension: "trabajadores", tema: "No trabajo infantil ni forzado", peso: 3, fuenteSugerida: "rrhh" },
  { dimension: "trabajadores", tema: "Salarios y categorías dentro de convenio (CCT)", peso: 2, fuenteSugerida: "rrhh" },
  { dimension: "trabajadores", tema: "Capacitación regular del personal", peso: 1, fuenteSugerida: "rrhh" },
  { dimension: "trabajadores", tema: "Índices de seguridad e higiene bajo control (IF/IG)", peso: 2, fuenteSugerida: "hs" },
  { dimension: "trabajadores", tema: "EPP entregado y vigente", peso: 2, fuenteSugerida: "hs" },
  { dimension: "trabajadores", tema: "Matriz de riesgos laborales actualizada", peso: 1, fuenteSugerida: "hs" },
  { dimension: "trabajadores", tema: "Beneficios por encima del mínimo legal", peso: 1, fuenteSugerida: "manual" },
  { dimension: "trabajadores", tema: "Canal de feedback / queja del empleado", peso: 1, fuenteSugerida: "manual" },
  { dimension: "trabajadores", tema: "Diversidad en la dotación", peso: 1, fuenteSugerida: "manual" },
  // ── COMUNIDAD ──
  { dimension: "comunidad", tema: "Proveedores locales priorizados", peso: 1, fuenteSugerida: "manual" },
  { dimension: "comunidad", tema: "Diversidad / inclusión en la cadena de proveedores", peso: 1, fuenteSugerida: "manual" },
  { dimension: "comunidad", tema: "Donaciones o voluntariado corporativo", peso: 1, fuenteSugerida: "manual" },
  { dimension: "comunidad", tema: "Impacto positivo documentado en la comunidad local", peso: 1, fuenteSugerida: "manual" },
  // ── AMBIENTE ──
  { dimension: "ambiente", tema: "Aspectos e impactos ambientales identificados", peso: 2, fuenteSugerida: "sustentabilidad" },
  { dimension: "ambiente", tema: "Gestión de residuos (incl. peligrosos) documentada", peso: 2, fuenteSugerida: "sustentabilidad" },
  { dimension: "ambiente", tema: "Cumplimiento legal ambiental verificado", peso: 2, fuenteSugerida: "sustentabilidad" },
  { dimension: "ambiente", tema: "Huella de carbono medida (alcances 1/2/3)", peso: 2, fuenteSugerida: "huellacarbono" },
  { dimension: "ambiente", tema: "Objetivos ambientales con progreso", peso: 1, fuenteSugerida: "sustentabilidad" },
  { dimension: "ambiente", tema: "Uso de energías renovables o eficiencia energética", peso: 1, fuenteSugerida: "manual" },
  // ── CLIENTES ──
  { dimension: "clientes", tema: "Política de privacidad y seguridad de datos de clientes", peso: 2, fuenteSugerida: "manual" },
  { dimension: "clientes", tema: "Marketing ético / veraz", peso: 1, fuenteSugerida: "manual" },
  { dimension: "clientes", tema: "Mecanismo de feedback / reclamo del cliente", peso: 1, fuenteSugerida: "manual" },
  { dimension: "clientes", tema: "Garantías claras y accesibles", peso: 1, fuenteSugerida: "manual" },
  { dimension: "clientes", tema: "Satisfacción del cliente medida", peso: 1, fuenteSugerida: "manual" },
];

function fechaHoy() { return new Date().toISOString().slice(0, 10); }

// ── Lectura automática desde otros módulos (mismo patrón que
// HuellaCarbono.js leerDatosAuto(): lee localStorage de otros módulos,
// nunca escribe sola — arma un resumen con trazabilidad de origen que
// el usuario confirma antes de aplicar) ──
function leerDatosAutoEticaRSE() {
  const resumen = { rrhh: null, hs: null, sustentabilidad: null, huellacarbono: null, fuentes: [] };

  // RRHH
  try {
    const empleados = JSON.parse(localStorage.getItem("alqos_rrhh") || "[]");
    const activos = empleados.filter(e => e.estado === "activo");
    const menores = activos.filter(e => {
      if (!e.fechaNacimiento) return false;
      const edad = Math.floor((new Date() - new Date(e.fechaNacimiento)) / (1000 * 60 * 60 * 24 * 365));
      return edad < 18;
    });
    const conFormacion = activos.filter(e => e.nivelEstudios || e.titulo || e.competencias);
    resumen.rrhh = {
      totalActivos: activos.length,
      menoresDetectados: menores.length,
      pctConFormacionRegistrada: activos.length ? Math.round((conFormacion.length / activos.length) * 100) : null,
    };
    resumen.fuentes.push({ origen: "RRHH", detalle: `${activos.length} empleados activos · ${menores.length} menores de 18 detectados · ${resumen.rrhh.pctConFormacionRegistrada ?? "—"}% con formación registrada` });
  } catch (e) { console.warn("EticaRSE: no se pudo leer alqos_rrhh", e); }

  // Higiene y Seguridad
  try {
    const hs = JSON.parse(localStorage.getItem("alqos_hs") || "{}");
    const registros = hs.registros || [];
    const epps = hs.epps || [];
    const riesgos = hs.riesgos || [];
    const hoy = new Date();
    const añoActual = hoy.getFullYear();
    const accidentesAño = registros.filter(r => r.tipo === "accidente" && new Date(r.fecha).getFullYear() === añoActual).length;
    const eppsVencidos = epps.filter(e => e.vencimiento && new Date(e.vencimiento) < hoy).length;
    const riesgosCriticos = riesgos.filter(r => r.nivel === "critico" || r.nivel === "alto").length;
    resumen.hs = { accidentesAño, eppsVencidos, riesgosCriticos, totalEpp: epps.length, totalRiesgos: riesgos.length };
    resumen.fuentes.push({ origen: "Higiene y Seguridad", detalle: `${accidentesAño} accidentes en ${añoActual} · ${eppsVencidos} EPP vencidos de ${epps.length} · ${riesgosCriticos} riesgos altos/críticos de ${riesgos.length}` });
  } catch (e) { console.warn("EticaRSE: no se pudo leer alqos_hs", e); }

  // Sustentabilidad
  try {
    const aspectos = JSON.parse(localStorage.getItem("alqos_sust_aspectos") || "[]");
    const legales = JSON.parse(localStorage.getItem("alqos_sust_legal") || "[]");
    const objetivos = JSON.parse(localStorage.getItem("alqos_sust_objetivos") || "[]");
    const cumplimientoLegal = legales.length ? Math.round((legales.filter(l => l.estado === "cumple").length / legales.length) * 100) : null;
    resumen.sustentabilidad = { aspectosRegistrados: aspectos.length, requisitosLegales: legales.length, cumplimientoLegalPct: cumplimientoLegal, objetivosActivos: objetivos.length };
    resumen.fuentes.push({ origen: "Sustentabilidad y MA", detalle: `${aspectos.length} aspectos ambientales · ${cumplimientoLegal ?? "—"}% cumplimiento legal (${legales.length} requisitos) · ${objetivos.length} objetivos` });
  } catch (e) { console.warn("EticaRSE: no se pudo leer alqos_sust_*", e); }

  // Huella de Carbono
  try {
    const hc = JSON.parse(localStorage.getItem("alqos_huella_carbono") || "{}");
    const tieneDatos = hc.consumos && Object.keys(hc.consumos).length > 0;
    resumen.huellacarbono = { periodo: hc.periodo || null, tieneDatos, meta: hc.meta || null };
    if (tieneDatos) resumen.fuentes.push({ origen: "Huella de Carbono", detalle: `Período ${hc.periodo || "—"} con consumos cargados${hc.meta ? ` · meta ${hc.meta} tCO₂eq` : ""}` });
  } catch (e) { console.warn("EticaRSE: no se pudo leer alqos_huella_carbono", e); }

  return resumen;
}

export default function EticaRSE({ onVolver, oscuro, usuario }) {
  const [items, setItems] = useState(() => {
    const guardados = storage.get(STORAGE_ITEMS);
    if (guardados.length > 0) return guardados;
    // primera vez: sembrar con los ítems de referencia
    return ITEMS_SEED.map((s, i) => ({ ...s, id: Date.now() + i, estado: "pendiente", evidencia: "", origen: "manual", fuenteAuto: null }));
  });
  const [divulgacion, setDivulgacion] = useState(() => storage.get(STORAGE_DIVULGACION));
  const [tab, setTab] = useState("resumen");
  const [mostrarAuto, setMostrarAuto] = useState(false);
  const [formDivulgacion, setFormDivulgacion] = useState({ tipo: "multa", fecha: fechaHoy() });
  const [formNuevoItem, setFormNuevoItem] = useState({ peso: 1 });
  const [editando, setEditando] = useState(null); // id del item en edición inline

  const datosAuto = useMemo(() => leerDatosAutoEticaRSE(), []);
  const esAdmin = usuario?.rol === "Administrador";

  const bg = oscuro ? "#09184D" : Q.grisPalido;
  const cardBg = oscuro ? "#0d2160" : Q.blanco;
  const texto = oscuro ? "#EDF2FA" : Q.grisOscuro;
  const borde = oscuro ? "#1e3a8a" : Q.grisClaro;
  const navBg = oscuro ? "#060f30" : Q.azul;
  const subtexto = oscuro ? "#9aabd4" : Q.grisPlata;
  const card = { background: cardBg, borderRadius: "14px", padding: "1.3rem", border: `1px solid ${borde}`, marginBottom: "12px" };
  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "11px", color: subtexto, fontWeight: "700", display: "block", marginTop: "10px", textTransform: "uppercase", letterSpacing: ".05em" };

  const guardarItems = (nuevos) => {
    setItems(nuevos);
    storage.set(STORAGE_ITEMS, nuevos);
    subirDatos(STORAGE_ITEMS);
  };
  const guardarDivulgacion = (nuevos) => {
    setDivulgacion(nuevos);
    storage.set(STORAGE_DIVULGACION, nuevos);
    subirDatos(STORAGE_DIVULGACION);
  };

  const actualizarItem = (id, cambios) => {
    guardarItems(items.map(it => it.id === id ? { ...it, ...cambios } : it));
  };

  const eliminarItem = async (id) => {
    if (!window.confirm("¿Eliminar este ítem del checklist?")) return;
    guardarItems(items.filter(it => it.id !== id));
    await eliminarDato(STORAGE_ITEMS, id);
  };

  const agregarItem = () => {
    if (!formNuevoItem.tema?.trim() || !formNuevoItem.dimension) { alert("Completá dimensión y tema."); return; }
    const nuevo = { id: Date.now(), dimension: formNuevoItem.dimension, tema: formNuevoItem.tema.trim(), peso: Number(formNuevoItem.peso) || 1, estado: "pendiente", evidencia: "", origen: "manual", fuenteAuto: null };
    guardarItems([...items, nuevo]);
    setFormNuevoItem({ peso: 1 });
  };

  const aplicarAuto = (campo, dimension, texto_) => {
    // Marca el/los ítems relacionados a esa fuente en esa dimensión como
    // "en revisión" con la evidencia auto-completada — el usuario todavía
    // tiene que revisar el estado (cumple/parcial/no cumple), nunca se
    // marca cumple en automático sin confirmación humana.
    const relacionados = items.filter(it => it.dimension === dimension && it.fuenteSugerida === campo);
    if (relacionados.length === 0) return;
    const nuevos = items.map(it =>
      relacionados.find(r => r.id === it.id)
        ? { ...it, evidencia: texto_, origen: "auto", fuenteAuto: campo, estado: it.estado === "pendiente" ? "parcial" : it.estado }
        : it
    );
    guardarItems(nuevos);
  };

  // ── Scoring por dimensión ──
  const scores = useMemo(() => {
    const resultado = {};
    Object.keys(DIMENSIONES).forEach(dim => {
      const itemsDim = items.filter(it => it.dimension === dim && it.estado !== "na");
      if (itemsDim.length === 0) { resultado[dim] = null; return; }
      const pesoTotal = itemsDim.reduce((a, it) => a + (Number(it.peso) || 1), 0);
      const puntosObtenidos = itemsDim.reduce((a, it) => a + ((Number(it.peso) || 1) * (ESTADOS[it.estado]?.puntos ?? 0)), 0);
      resultado[dim] = pesoTotal > 0 ? Math.round((puntosObtenidos / pesoTotal) * 100) : null;
    });
    return resultado;
  }, [items]);

  const scoreGlobal = useMemo(() => {
    const validos = Object.values(scores).filter(s => s !== null);
    if (!validos.length) return null;
    return Math.round(validos.reduce((a, s) => a + s, 0) / validos.length);
  }, [scores]);

  const colorScore = (s) => s === null ? subtexto : s >= 80 ? "#22c55e" : s >= 50 ? "#f59e0b" : "#e63946";

  const exportarCSV = () => {
    const rows = [["ÉTICA Y RSE — ALQOS™ (checklist propio, no oficial de ningún esquema)"], [`Puntaje global: ${scoreGlobal ?? "—"}/100`], [""],
      ["DIMENSIÓN", "TEMA", "PESO", "ESTADO", "EVIDENCIA", "ORIGEN"]];
    items.forEach(it => rows.push([DIMENSIONES[it.dimension]?.label, it.tema, it.peso, ESTADOS[it.estado]?.label, it.evidencia || "", it.origen === "auto" ? `Auto (${it.fuenteAuto})` : "Manual"]));
    if (divulgacion.length) {
      rows.push([""], ["CUESTIONARIO DE DIVULGACIÓN"], ["Tipo", "Descripción", "Cómo se abordó", "Medidas preventivas", "Fecha"]);
      divulgacion.forEach(d => rows.push([d.tipo, d.descripcion, d.comoSeAbordo || "", d.medidasPreventivas || "", d.fecha]));
    }
    const csv = rows.map(r => r.map(c => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `EticaRSE_${fechaHoy()}.csv`; a.click();
  };

  const guardarDivulgacionNueva = () => {
    if (!formDivulgacion.descripcion?.trim()) { alert("Describí el hecho."); return; }
    const nuevo = { ...formDivulgacion, id: Date.now(), cargadoPor: usuario?.nombre };
    guardarDivulgacion([...divulgacion, nuevo]);
    setFormDivulgacion({ tipo: "multa", fecha: fechaHoy() });
  };

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="eticarse" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="eticarse" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>🕊️ Ética y RSE</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => setMostrarAuto(m => !m)} style={{ padding: "6px 12px", background: "rgba(59,130,246,.3)", border: "1px solid rgba(59,130,246,.5)", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white", fontWeight: "600" }}>🔄 Datos auto</button>
          <button onClick={exportarCSV} style={{ padding: "6px 12px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📊 CSV</button>
        </div>
      </div>

      <div style={{ padding: "1.2rem 1.5rem", maxWidth: "900px", margin: "0 auto" }}>
        {/* PANEL AUTO */}
        {mostrarAuto && (
          <div style={{ background: oscuro ? "#0a1830" : "#eff6ff", border: "2px solid #3b82f6", borderRadius: "14px", padding: "1.2rem", marginBottom: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#3b82f6", margin: 0 }}>🔄 Datos disponibles desde otros módulos</h3>
              <button onClick={() => setMostrarAuto(false)} style={{ background: "none", border: "none", color: subtexto, cursor: "pointer", fontSize: "18px" }}>×</button>
            </div>
            <p style={{ fontSize: "12px", color: subtexto, marginBottom: "12px" }}>Resumen calculado desde RRHH, Higiene y Seguridad, Sustentabilidad y Huella de Carbono. No se aplica nada automáticamente — revisá y confirmá por ítem.</p>
            {datosAuto.fuentes.length === 0 && <p style={{ fontSize: "12px", color: subtexto }}>Todavía no hay datos cargados en esos módulos.</p>}
            {datosAuto.fuentes.map((f, i) => (
              <div key={i} style={{ padding: "10px 12px", borderRadius: "8px", background: oscuro ? "#091840" : "white", border: `1px solid ${borde}`, marginBottom: "6px" }}>
                <span style={{ fontSize: "11px", fontWeight: "700", color: "#3b82f6" }}>{f.origen}</span>
                <p style={{ fontSize: "12px", color: texto, margin: "3px 0 0" }}>{f.detalle}</p>
              </div>
            ))}
            {datosAuto.rrhh && (
              <button onClick={() => aplicarAuto("rrhh", "trabajadores", `RRHH: ${datosAuto.rrhh.totalActivos} activos, ${datosAuto.rrhh.menoresDetectados} menores detectados, ${datosAuto.rrhh.pctConFormacionRegistrada ?? "—"}% con formación registrada. (auto, ${fechaHoy()})`)}
                style={{ marginTop: "8px", marginRight: "6px", padding: "7px 14px", background: "#3b82f6", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>✅ Volcar a Trabajadores</button>
            )}
            {datosAuto.hs && (
              <button onClick={() => aplicarAuto("hs", "trabajadores", `Higiene y Seguridad: ${datosAuto.hs.accidentesAño} accidentes año actual, ${datosAuto.hs.eppsVencidos} EPP vencidos de ${datosAuto.hs.totalEpp}, ${datosAuto.hs.riesgosCriticos} riesgos altos/críticos de ${datosAuto.hs.totalRiesgos}. (auto, ${fechaHoy()})`)}
                style={{ marginTop: "8px", marginRight: "6px", padding: "7px 14px", background: "#3b82f6", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>✅ Volcar EPP/Riesgos a Trabajadores</button>
            )}
            {datosAuto.sustentabilidad && (
              <button onClick={() => aplicarAuto("sustentabilidad", "ambiente", `Sustentabilidad: ${datosAuto.sustentabilidad.aspectosRegistrados} aspectos ambientales, ${datosAuto.sustentabilidad.cumplimientoLegalPct ?? "—"}% cumplimiento legal, ${datosAuto.sustentabilidad.objetivosActivos} objetivos activos. (auto, ${fechaHoy()})`)}
                style={{ marginTop: "8px", marginRight: "6px", padding: "7px 14px", background: "#22c55e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>✅ Volcar a Ambiente</button>
            )}
            {datosAuto.huellacarbono?.tieneDatos && (
              <button onClick={() => aplicarAuto("huellacarbono", "ambiente", `Huella de Carbono: período ${datosAuto.huellacarbono.periodo || "—"} con datos cargados${datosAuto.huellacarbono.meta ? `, meta ${datosAuto.huellacarbono.meta} tCO₂eq` : ""}. (auto, ${fechaHoy()})`)}
                style={{ marginTop: "8px", padding: "7px 14px", background: "#22c55e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>✅ Volcar Huella a Ambiente</button>
            )}
          </div>
        )}

        {/* TABS */}
        <div style={{ display: "flex", gap: "4px", marginBottom: "1rem", overflowX: "auto", paddingBottom: "4px" }}>
          {[["resumen", "📊 Resumen"], ...Object.entries(DIMENSIONES).map(([k, v]) => [k, `${v.icono} ${v.label}`]), ["divulgacion", "📢 Divulgación"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ padding: "7px 12px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap", background: tab === id ? Q.azulVivo : "transparent", color: tab === id ? "white" : subtexto, flexShrink: 0 }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── RESUMEN ── */}
        {tab === "resumen" && (
          <div>
            <div style={{ ...card, textAlign: "center", background: oscuro ? "#0a1f5c" : "#eff6ff", border: `2px solid ${colorScore(scoreGlobal)}40` }}>
              <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 4px", textTransform: "uppercase", letterSpacing: ".05em" }}>Puntaje global (escala propia ALQOS™ 0–100)</p>
              <p style={{ fontSize: "44px", fontWeight: "900", color: colorScore(scoreGlobal), margin: 0, lineHeight: 1 }}>{scoreGlobal ?? "—"}</p>
              <p style={{ fontSize: "11px", color: subtexto, marginTop: "6px" }}>Referencia inspirada en el umbral público de Sistema B (~80/100) — no es un puntaje oficial ni comparable 1:1 con el BIA real.</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: "10px", marginBottom: "1rem" }}>
              {Object.entries(DIMENSIONES).map(([k, v]) => (
                <div key={k} onClick={() => setTab(k)} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${borde}`, textAlign: "center", cursor: "pointer" }}>
                  <p style={{ fontSize: "20px", margin: "0 0 4px" }}>{v.icono}</p>
                  <p style={{ fontSize: "20px", fontWeight: "800", color: colorScore(scores[k]), margin: "0 0 2px" }}>{scores[k] ?? "—"}</p>
                  <p style={{ fontSize: "11px", color: subtexto, margin: 0 }}>{v.label}</p>
                </div>
              ))}
            </div>
            <div style={card}>
              <h3 style={{ fontSize: "13px", fontWeight: "700", color: texto, marginBottom: "10px" }}>Estado del checklist</h3>
              {["cumple", "parcial", "no_cumple", "pendiente", "na"].map(est => {
                const cant = items.filter(it => it.estado === est).length;
                if (!cant) return null;
                return (
                  <div key={est} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `0.5px solid ${borde}`, fontSize: "12px" }}>
                    <span style={{ color: ESTADOS[est].color, fontWeight: "600" }}>{ESTADOS[est].label}</span>
                    <span style={{ color: texto }}>{cant} de {items.length} ítems</span>
                  </div>
                );
              })}
            </div>
            {divulgacion.length > 0 && (
              <div style={{ ...card, border: "1.5px solid #e6394640" }}>
                <h3 style={{ fontSize: "13px", fontWeight: "700", color: "#e63946", marginBottom: "6px" }}>📢 Divulgación cargada</h3>
                <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>{divulgacion.length} evento(s) registrados en el cuestionario de divulgación — revisalos en esa pestaña antes de cualquier auditoría.</p>
              </div>
            )}
          </div>
        )}

        {/* ── DIMENSIÓN (checklist) ── */}
        {Object.keys(DIMENSIONES).includes(tab) && (
          <div>
            <div style={{ ...card, background: `${DIMENSIONES[tab].color}10`, border: `1px solid ${DIMENSIONES[tab].color}40` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ fontSize: "14px", fontWeight: "700", color: DIMENSIONES[tab].color, margin: 0 }}>{DIMENSIONES[tab].icono} {DIMENSIONES[tab].label}</h3>
                <span style={{ fontSize: "18px", fontWeight: "800", color: colorScore(scores[tab]) }}>{scores[tab] ?? "—"}/100</span>
              </div>
            </div>
            {items.filter(it => it.dimension === tab).map(it => (
              <div key={it.id} style={{ ...card, borderLeft: `4px solid ${ESTADOS[it.estado].color}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: "13px", fontWeight: "700", color: texto, margin: "0 0 2px" }}>{it.tema}</p>
                    <p style={{ fontSize: "11px", color: subtexto, margin: 0 }}>Peso {it.peso} {it.origen === "auto" && <span style={{ color: "#3b82f6", fontWeight: "700" }}>· AUTO ({it.fuenteAuto})</span>}</p>
                  </div>
                  {esAdmin && <button onClick={() => eliminarItem(it.id)} style={{ background: "none", border: "none", color: "#e63946", cursor: "pointer", fontSize: "16px" }}>×</button>}
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" }}>
                  {Object.entries(ESTADOS).map(([val, { label, color }]) => (
                    <button key={val} onClick={() => actualizarItem(it.id, { estado: val })}
                      style={{ padding: "5px 10px", borderRadius: "8px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: it.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: it.estado === val ? `${color}22` : cardBg, color: it.estado === val ? color : subtexto }}>
                      {label}
                    </button>
                  ))}
                </div>
                {editando === it.id ? (
                  <div style={{ marginTop: "8px" }}>
                    <textarea rows={2} defaultValue={it.evidencia} onBlur={e => { actualizarItem(it.id, { evidencia: e.target.value }); setEditando(null); }}
                      style={{ ...inp, marginTop: 0, resize: "vertical" }} placeholder="Evidencia / notas..." autoFocus />
                  </div>
                ) : (
                  <p onClick={() => setEditando(it.id)} style={{ fontSize: "12px", color: it.evidencia ? texto : subtexto, marginTop: "8px", cursor: "text", padding: "6px 8px", background: oscuro ? "#091840" : "#f8fafc", borderRadius: "6px" }}>
                    {it.evidencia || "Click para agregar evidencia / notas..."}
                  </p>
                )}
              </div>
            ))}
            {esAdmin && (
              <div style={{ ...card, background: oscuro ? "#0a1f0a" : "#f0fdf4", border: "1px solid #22c55e40" }}>
                <h3 style={{ fontSize: "12px", fontWeight: "700", color: "#22c55e", marginBottom: "8px" }}>+ Agregar ítem a {DIMENSIONES[tab].label}</h3>
                <input value={formNuevoItem.dimension === tab ? (formNuevoItem.tema || "") : ""} onChange={e => setFormNuevoItem(f => ({ ...f, dimension: tab, tema: e.target.value }))}
                  style={{ ...inp, marginTop: 0 }} placeholder="Descripción del tema a evaluar" />
                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <input type="number" min="1" max="5" value={formNuevoItem.peso} onChange={e => setFormNuevoItem(f => ({ ...f, peso: e.target.value }))} style={{ ...inp, marginTop: 0, width: "80px" }} placeholder="Peso" />
                  <button onClick={agregarItem} style={{ padding: "8px 16px", background: "#22c55e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "700" }}>+ Agregar</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── DIVULGACIÓN ── */}
        {tab === "divulgacion" && (
          <div>
            <div style={{ ...card, background: oscuro ? "#1a0505" : "#fff5f5", border: "1px solid #e6394640" }}>
              <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>Multas, sanciones, demandas o prácticas polémicas. Todos los esquemas de auditoría social piden transparencia acá — respuesta afirmativa no descalifica sola, pero exige describir cómo se abordó y qué medidas preventivas se tomaron.</p>
            </div>
            {esAdmin && (
              <div style={card}>
                <span style={lbl}>Tipo</span>
                <select value={formDivulgacion.tipo} onChange={e => setFormDivulgacion(f => ({ ...f, tipo: e.target.value }))} style={inp}>
                  <option value="multa">Multa</option>
                  <option value="sancion">Sanción</option>
                  <option value="demanda">Demanda</option>
                  <option value="practica_polemica">Práctica polémica</option>
                  <option value="otro">Otro</option>
                </select>
                <span style={lbl}>Descripción del hecho</span>
                <textarea rows={2} value={formDivulgacion.descripcion || ""} onChange={e => setFormDivulgacion(f => ({ ...f, descripcion: e.target.value }))} style={{ ...inp, resize: "vertical" }} />
                <span style={lbl}>Cómo se abordó</span>
                <textarea rows={2} value={formDivulgacion.comoSeAbordo || ""} onChange={e => setFormDivulgacion(f => ({ ...f, comoSeAbordo: e.target.value }))} style={{ ...inp, resize: "vertical" }} />
                <span style={lbl}>Medidas preventivas</span>
                <textarea rows={2} value={formDivulgacion.medidasPreventivas || ""} onChange={e => setFormDivulgacion(f => ({ ...f, medidasPreventivas: e.target.value }))} style={{ ...inp, resize: "vertical" }} />
                <span style={lbl}>Fecha</span>
                <input type="date" value={formDivulgacion.fecha} onChange={e => setFormDivulgacion(f => ({ ...f, fecha: e.target.value }))} style={inp} />
                <button onClick={guardarDivulgacionNueva} style={{ marginTop: "12px", width: "100%", padding: "11px", background: "#e63946", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "13px", fontWeight: "700" }}>Registrar</button>
              </div>
            )}
            {divulgacion.length === 0
              ? <div style={{ textAlign: "center", padding: "3rem" }}><p style={{ fontSize: "36px" }}>✅</p><p style={{ fontSize: "13px", color: subtexto }}>Sin eventos registrados</p></div>
              : divulgacion.map(d => (
                <div key={d.id} style={{ ...card, borderLeft: "4px solid #e63946" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#e63946", textTransform: "uppercase" }}>{d.tipo.replace("_", " ")}</span>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <span style={{ fontSize: "11px", color: subtexto }}>{d.fecha}</span>
                      {esAdmin && <button onClick={async () => { if (!window.confirm("¿Eliminar?")) return; guardarDivulgacion(divulgacion.filter(x => x.id !== d.id)); await eliminarDato(STORAGE_DIVULGACION, d.id); }} style={{ background: "none", border: "none", color: "#e63946", cursor: "pointer" }}>×</button>}
                    </div>
                  </div>
                  <p style={{ fontSize: "13px", color: texto, margin: "6px 0 0" }}>{d.descripcion}</p>
                  {d.comoSeAbordo && <p style={{ fontSize: "12px", color: subtexto, margin: "6px 0 0" }}><b>Abordaje:</b> {d.comoSeAbordo}</p>}
                  {d.medidasPreventivas && <p style={{ fontSize: "12px", color: subtexto, margin: "4px 0 0" }}><b>Prevención:</b> {d.medidasPreventivas}</p>}
                </div>
              ))
            }
          </div>
        )}

        <p style={{ fontSize: "11px", color: subtexto, textAlign: "center", marginTop: "1rem" }}>
          ALQOS™ by QORVEX · Ética y RSE · Checklist propio — no es el cuestionario oficial de Sistema B, SMETA, Disney FAMA ni ningún otro esquema
        </p>
      </div>
    </div>
  );
}
